
/**
 * Production configuration and security settings
 * Centralizes all production vs development behavior
 * 
 * VERSION: 1.0.0 - Production Ready
 * RELEASE: Monte Carlo Production Launch
 */

import { APP_VERSION } from './version';

// Domain configuration constants
const PRODUCTION_DOMAIN = 'tools.wealthvantagepartners.com';

// Reliable environment detection (doesn't depend on NODE_ENV)
const isReliablyDevelopment = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

const isReliablyProduction = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === PRODUCTION_DOMAIN || 
         (!hostname.includes('localhost') && 
          !hostname.includes('lovable.app') && 
          !hostname.includes('127.0.0.1'));
};

// Helper function to get base URL (environment-aware)
const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    // Use the current window location for dynamic environments
    return `${window.location.protocol}//${window.location.host}`;
  }
  
  // Fallback for server-side
  return `https://${PRODUCTION_DOMAIN}`;
};

export const PRODUCTION_CONFIG = {
  // Version information
  VERSION: APP_VERSION.version,
  FULL_VERSION: APP_VERSION.fullVersion,
  RELEASE_NAME: APP_VERSION.release.name,
  BUILD_DATE: new Date(APP_VERSION.build),
  
  // Environment detection (reliable, doesn't depend on NODE_ENV)
  isDevelopment: isReliablyDevelopment,
  isProduction: isReliablyProduction,
  
  // Safe mode detection (for emergency fallback)
  isSafeMode: () => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('safe') === '1';
  },

  // Maintenance mode detection (project pause)
  isMaintenanceMode: () => {
    if (typeof window === 'undefined') return true; // Default to maintenance during SSR/build
    const params = new URLSearchParams(window.location.search);
    // Allow explicit override via ?maintenance=0 for admin access
    if (params.get('maintenance') === '0') return false;
    // Safe mode also bypasses maintenance page so the app remains accessible
    if (params.get('safe') === '1') return false;
    return true; // Paused by default
  },

  // Domain configuration
  PRODUCTION_DOMAIN,
  
  // Base URL helper function
  getBaseUrl,

  // Security settings
  SECURITY: {
    // Admin bypass for specific domains/emails (security-controlled)
    ADMIN_BYPASS_ENABLED: true,
    ADMIN_BYPASS_DOMAINS: [
      'localhost', 
      'lovable.app', 
      PRODUCTION_DOMAIN,
      'wealthvantagepartners.com',
      'www.wealthvantagepartners.com'
    ],
    ADMIN_BYPASS_EMAILS: ['admin@wealthvantagepartners.com', 'walshbenjamin007@gmail.com'],
    
    // Rate limiting
    MAX_LOGIN_ATTEMPTS: 5,
    LOGIN_COOLDOWN_MINUTES: 15,
    
    // Session timeouts
    SESSION_TIMEOUT_HOURS: 8,
    IDLE_TIMEOUT_MINUTES: 30,
    
    // Logging levels
    SENSITIVE_DATA_LOGGING: false, // Never log sensitive data in production
    DEBUG_LOGGING: false, // Disable debug logs in production
  },

  // License validation settings
  LICENSE_VALIDATION: {
    // Controlled admin bypass with domain/email restrictions
    ADMIN_BYPASS_LOCALHOST_ONLY: true,  // Enable for localhost development
    ALLOW_DEMO_BYPASS: true,           // Enable demo bypass
    VALIDATION_TIMEOUT_MS: 10000,
    RETRY_ATTEMPTS: 2,
  },

  // Compliance and branding
  COMPLIANCE: {
    AUSTRALIAN_DATA_COMPLIANT: true,
    SHOW_AUSTRALIAN_COMPLIANCE: true,
    COPYRIGHT_TEXT: 'Copyright WealthVantage Partners',
    MADE_IN_TEXT: 'Designed and made in Australia',
  },

  // Stripe configuration
  STRIPE: {
    SUCCESS_URL: `${getBaseUrl()}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
    CANCEL_URL: `${getBaseUrl()}/billing/cancel`,
  },

  // API settings
  API: {
    TIMEOUT_MS: 30000,
    MAX_RETRIES: 3,
    ENABLE_DEBUG_LOGS: false,
  },

  // Feature flags for production
  FEATURES: {
    ENABLE_ANALYTICS: true,
    ENABLE_ERROR_REPORTING: true,
    ENABLE_PERFORMANCE_MONITORING: true,
  },

  // Test Suite configuration
  TEST_SUITE: {
    // Mode: 'hidden' hides the tab, 'mock' shows demo data, 'live' runs real checks
    MODE: (typeof window !== 'undefined' && isReliablyProduction()) ? 'hidden' : 'mock',
    SHOW_DEMO_LABEL: true,
    SLA_MS: {
      readP95: 500,
      writeP95: 1000,
      pingP95: 100
    }
  }
};

// Type-safe configuration access
export type ProductionConfig = typeof PRODUCTION_CONFIG;

// Helper functions for common checks
export const isSecureEnvironment = () => PRODUCTION_CONFIG.isProduction();
export const shouldLogSensitiveData = () => PRODUCTION_CONFIG.SECURITY.SENSITIVE_DATA_LOGGING;
export const shouldEnableDebugLogs = () => PRODUCTION_CONFIG.SECURITY.DEBUG_LOGGING;
